import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import type { Card as CardDto, CardType, HeaderStyle, Stage } from "../../types/domain";
import type { DayLabeled } from "../../lib/recomputeDays";
import { BoardCard } from "./Card";
import { IcoCheck, IcoEdit, IcoPlus, IcoTrash } from "../../components/icons";

const STAGE_COLOR: Record<DayLabeled["stage"], string> = {
  warmup: "var(--stage-warmup)",
  sales: "var(--stage-sales)",
  close: "var(--stage-close)",
};

const STAGE_LABEL: Record<DayLabeled["stage"], string> = {
  warmup: "Прогрев",
  sales: "Продажи",
  close: "Закрытие",
};

const STAGE_MENU: Array<{ id: Stage; label: string; color: string }> = [
  { id: "warmup", label: "Прогрев", color: "var(--stage-warmup)" },
  { id: "sales", label: "Продажи / эфир", color: "var(--stage-sales)" },
  { id: "close", label: "Дожим / закрытие", color: "var(--stage-close)" },
];

const pluralEmails = (n: number): string => {
  if (n === 1) return "письмо";
  if (n > 0 && n < 5) return "письма";
  return "писем";
};

interface BoardColumnProps {
  day: DayLabeled;
  cards: CardDto[];
  types: CardType[];
  headerStyle: HeaderStyle;
  draggingCardId: string | null;
  canDelete: boolean;
  /** Полный CRUD на доске (editor/admin). Управляет UI дней (rename/menu),
   *  добавлением дней. */
  canWrite: boolean;
  /** Право создавать карточки. Шире, чем canWrite: own-scope/custom роли с
   *  cards.create тоже видят кнопку, хотя меню дня им недоступно. */
  canCreateCard: boolean;
  cardCreationBusy: boolean;
  onOpenCard: (card: CardDto) => void;
  onDeleteCard: (card: CardDto) => void;
  onAddCard: (dayKey: string) => void;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onDrop: (dayKey: string, targetIndex: number, event: DragEvent<HTMLDivElement>) => void;
  onAddDayBefore: () => void;
  onAddDayAfter: () => void;
  onChangeStage: (stage: Stage) => void;
  onRenameDay: (customLabel: string | null) => void;
  onDeleteDay: () => void;
  /** Any day-level mutation in flight — lock menu actions so click positions stay fresh. */
  dayMutationBusy: boolean;
}

export function BoardColumn({
  day,
  cards,
  types,
  headerStyle,
  draggingCardId,
  canDelete,
  canWrite,
  canCreateCard,
  cardCreationBusy,
  onOpenCard,
  onDeleteCard,
  onAddCard,
  onDragStartCard,
  onDragEndCard,
  onDrop,
  onAddDayBefore,
  onAddDayAfter,
  onChangeStage,
  onRenameDay,
  onDeleteDay,
  dayMutationBusy,
}: BoardColumnProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);
  const [over, setOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");

  // Window-level dragend/drop guarantees we clear the highlight even if the user
  // released the mouse outside any column (HTML5 DnD doesn't fire our local drop then).
  useEffect(() => {
    const reset = () => {
      dragDepth.current = 0;
      setOver(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, []);

  // What stageLabel would be without customLabel — used to detect no-op renames
  // so that typing back the auto-label clears the override instead of persisting it.
  const autoStageLabel = useMemo(() => {
    if (day.stage === "warmup") {
      return day.dayNum === "−1" || day.dayNum === "-1" ? "Финальный прогрев" : "Прогрев";
    }
    if (day.stage === "sales") return `Эфир · День ${day.dayNum}`;
    if (day.stage === "close") return "Дожим";
    return day.stage;
  }, [day.stage, day.dayNum]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const startEditLabel = () => {
    setLabelDraft(day.customLabel || day.stageLabel || "");
    setEditingLabel(true);
    setMenuOpen(false);
    setTimeout(() => inputRef.current?.select(), 10);
  };

  const commitLabel = () => {
    const v = labelDraft.trim();
    onRenameDay(!v || v === autoStageLabel ? null : v);
    setEditingLabel(false);
  };

  const cancelLabel = () => setEditingLabel(false);

  const computeTargetIndex = (e: DragEvent<HTMLDivElement>): number => {
    const body = bodyRef.current;
    if (!body) return cards.length;
    const nodes = Array.from(
      body.querySelectorAll<HTMLElement>(".card[data-card-id]"),
    );
    const y = e.clientY;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]!;
      if (draggingCardId && node.dataset.cardId === draggingCardId) continue;
      const rect = node.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) return i;
    }
    return cards.length;
  };

  const handleDragEnter = () => {
    // Counter-based tracking: dragenter/dragleave fire once per element boundary
    // crossing (including child cards), so a simple boolean flickers. Counting
    // enters/leaves and showing the highlight while depth > 0 is the standard fix.
    dragDepth.current += 1;
    if (!over) setOver(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    // preventDefault allows drop; we accept both card moves and template drops.
    e.preventDefault();
  };

  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setOver(false);
    const idx = computeTargetIndex(e);
    onDrop(day.id, idx, e);
  };

  const emailCount = cards.filter((c) => {
    const t = types.find((x) => x.id === c.typeId);
    return t && /письм|email/i.test(t.name);
  }).length;

  const titleText = headerStyle === "minimal" ? day.weekday : day.stageLabel;

  return (
    <div className="column">
      <div className={"column-head" + (day.isToday ? " is-today" : "")}>
        <div className="col-date-row">
          <span className="col-weekday">{day.weekday}</span>
          <div className="col-date-right">
            {day.isToday ? (
              <span className="today-badge">
                <span className="live-dot" />
                Сегодня
              </span>
            ) : (
              <span className="col-date">{day.date}</span>
            )}
            {canWrite && <div className="col-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="col-menu-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                title="Настройки дня"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="5" cy="12" r="1.2" />
                  <circle cx="12" cy="12" r="1.2" />
                  <circle cx="19" cy="12" r="1.2" />
                </svg>
              </button>
              {menuOpen && (
                <div className="col-dropdown">
                  <div className="col-dropdown-section">Добавить день</div>
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      if (dayMutationBusy) return;
                      onAddDayBefore();
                      setMenuOpen(false);
                    }}
                    disabled={dayMutationBusy}
                  >
                    <IcoPlus size={13} />
                    Слева от этого дня
                  </button>
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      if (dayMutationBusy) return;
                      onAddDayAfter();
                      setMenuOpen(false);
                    }}
                    disabled={dayMutationBusy}
                  >
                    <IcoPlus size={13} />
                    Справа от этого дня
                  </button>
                  <div className="col-dropdown-section">Этап</div>
                  {STAGE_MENU.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={
                        "col-dropdown-item" + (day.stage === s.id ? " active" : "")
                      }
                      onClick={() => {
                        if (dayMutationBusy) return;
                        if (day.stage !== s.id) onChangeStage(s.id);
                        setMenuOpen(false);
                      }}
                      disabled={dayMutationBusy}
                    >
                      <span className="stage-dot" style={{ background: s.color }} />
                      {s.label}
                      {day.stage === s.id && (
                        <IcoCheck size={12} stroke="var(--accent)" sw={2.5} />
                      )}
                    </button>
                  ))}
                  <div className="col-dropdown-sep" />
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={startEditLabel}
                  >
                    <IcoEdit size={13} />
                    Переименовать день
                  </button>
                  {day.customLabel && (
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={() => {
                        onRenameDay(null);
                        setMenuOpen(false);
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        strokeLinecap="round"
                      >
                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                        <path d="M3 3v5h5" />
                      </svg>
                      Сбросить название
                    </button>
                  )}
                  {canDelete && (
                    <>
                      <div className="col-dropdown-sep" />
                      <button
                        type="button"
                        className="col-dropdown-item col-dropdown-danger"
                        onClick={() => {
                          if (dayMutationBusy) return;
                          onDeleteDay();
                          setMenuOpen(false);
                        }}
                        disabled={dayMutationBusy}
                      >
                        <IcoTrash size={13} />
                        Удалить день
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>}
          </div>
        </div>
        <div className="col-title">
          {headerStyle === "full" && <span className="col-day-num">D{day.dayNum}</span>}
          {editingLabel ? (
            <input
              ref={inputRef}
              className="col-title-input"
              value={labelDraft}
              autoFocus
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitLabel();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelLabel();
                }
              }}
              placeholder={headerStyle === "minimal" ? day.weekday : day.stageLabel}
            />
          ) : (
            <span
              className="col-title-text"
              title={canWrite ? "Кликните, чтобы переименовать" : undefined}
              onClick={canWrite ? startEditLabel : undefined}
            >
              {titleText}
            </span>
          )}
        </div>
        {headerStyle === "full" && (
          <div className="col-stage">
            <span className="stage-dot" style={{ background: STAGE_COLOR[day.stage] }} />
            {STAGE_LABEL[day.stage]}
          </div>
        )}
        <div className="col-meta-row">
          <span className="col-count">
            <b>{emailCount}</b> {pluralEmails(emailCount)}
          </span>
          {canCreateCard && (
            <button
              type="button"
              className="col-add"
              onClick={() => onAddCard(day.id)}
              disabled={cardCreationBusy}
            >
              <IcoPlus size={12} /> Добавить
            </button>
          )}
        </div>
      </div>
      <div
        ref={bodyRef}
        className={"col-body" + (over ? " drag-over" : "")}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {cards.map((c) => (
          <BoardCard
            key={c.id}
            card={c}
            type={types.find((t) => t.id === c.typeId) ?? types[0]!}
            onOpen={() => onOpenCard(c)}
            onDelete={() => onDeleteCard(c)}
            onDragStart={() => onDragStartCard(c.id)}
            onDragEnd={onDragEndCard}
            isDragging={draggingCardId === c.id}
            draggable={canWrite}
          />
        ))}
        {cards.length === 0 && canCreateCard && (
          <button
            type="button"
            className="card-add-empty"
            onClick={() => onAddCard(day.id)}
            disabled={cardCreationBusy}
          >
            <IcoPlus size={14} /> Добавить карточку
          </button>
        )}
      </div>
    </div>
  );
}
